import { useMemo } from 'react';
import { useHomeContext } from '@views/Home/HomeProvider/HomeContext';

import { PageQuickAccessBar } from '@/components/common/Providers/GraphqlProvider/generated';
import { BlockColor } from '@/components/blocks/PageQuickAccessBar/admin/components/GeneralInformationQuickAccessBar';

import Mobile from './Mobile';
import DesktopStyleA from './StyleA';
import DesktopStyleB from './StyleB';
import { getWrapperBackgroundColor } from './utils';

const QuickAccessBar = ({
  currentPageQuickAccessBar,
}: {
  currentPageQuickAccessBar?: PageQuickAccessBar;
}) => {
  const {
    home: { quickAccessBar: quickAccessBarContext },
  } = useHomeContext();

  const quickAccessBar = currentPageQuickAccessBar || quickAccessBarContext;
  const isCarousel = quickAccessBar?.isCarrousel;
  const itemsPerView = quickAccessBar?.itemsPerView || 4;
  const isLightBackground =
    quickAccessBar?.color === BlockColor.WHITE || quickAccessBar?.color === BlockColor.GRAY;

  // 🔄 Memoización de items ordenados (reutilizable)
  const sortedItems = useMemo(
    () => [...(quickAccessBar?.items ?? [])]?.sort((a: any, b: any) => a.orderIndex - b.orderIndex),
    [quickAccessBar?.items]
  );

  const quickAccessBarData = {
    quickAccessBar,
    isCarousel,
    itemsPerView,
    isLightBackground,
    sortedItems,
  };

  if (quickAccessBar?.isDisabled) return null;

  // Switch para manejar diferentes estilos
  const renderByStyle = () => {
    switch (quickAccessBar?.pageBlockStyle) {
      case 'B':
        return <DesktopStyleB {...quickAccessBarData} />;
      case 'C':
        return (
          <section className={'mt-10'}>
            <div>Estilo C - Por implementar</div>
          </section>
        );
      case 'A':
      default:
        return <DesktopStyleA {...quickAccessBarData} />;
    }
  };

  return (
    <>
      <div className="block md:hidden">
        <Mobile {...quickAccessBarData} />
      </div>
      <div className="hidden md:block">{renderByStyle()}</div>
    </>
  );
};

export default QuickAccessBar;

export function QuickAccessBarSkeleton({ block }: { block?: PageQuickAccessBar | any }) {
  const items = block?.items ?? [];
  const itemsCount = items.length || block?.itemsPerView || 4;

  const isLightBackground = block?.color === BlockColor.WHITE || block?.color === BlockColor.GRAY;

  const bgClass = isLightBackground ? 'bg-gray-200' : 'bg-gray-300';

  const styleSize = (() => {
    switch (block?.pageBlockStyle) {
      case 'B':
        return 'h-28';
      case 'C':
        return 'h-24';
      case 'A':
      default:
        return 'h-20';
    }
  })();

  // If block is carousel, render a single horizontal row where `itemsPerView` determines visible width
  if (block?.isCarrousel) {
    const perView = block?.itemsPerView || 3;
    const renderCount = Math.min(itemsCount || perView, 12);
    return (
      <section className={`${getWrapperBackgroundColor(block?.color)} pb-[20px] pt-6 md:pt-10`}>
        <div className="animate-pulse">
          <div className="inner-extend">
            <div className="mb-2 h-4 w-48 rounded bg-gray-200" />
            <div className="flex gap-4 overflow-x-auto py-2">
              {Array.from({ length: renderCount }).map((_, i) => (
                <div
                  key={i}
                  className={`${styleSize} rounded-md ${bgClass} flex-shrink-0`}
                  style={{ flex: `0 0 ${100 / perView}%` }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Default: render as grid. For style B we want 3 per row.
  const cols = block?.pageBlockStyle === 'B' ? 3 : Math.min(block?.itemsPerView || 4, 4);
  const renderCount = Math.min(itemsCount || cols * 2, 15);

  return (
    <section className={`${getWrapperBackgroundColor(block?.color)} pb-[20px] pt-6 md:pt-10`}>
      <div className="animate-pulse">
        <div className="inner-extend">
          <div className="mb-2 h-4 w-48 rounded bg-gray-200" />
          <div
            className="grid gap-4 py-2"
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
          >
            {Array.from({ length: renderCount }).map((_, i) => (
              <div key={i} className={`${styleSize} w-full rounded-md ${bgClass}`} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
